import { Link, useParams } from "react-router-dom";
import { Button, ErrorText, Panel, Spinner } from "../../components/ui";
import { fmtAmount, fmtDate, fmtPct, fmtPrice, fmtRate } from "../../lib/format";
import { PORTFOLIO_KIND_LABEL } from "./api";
import { usePortfolio, usePositions } from "./hooks";

function pnlClass(value: string | null): string {
  if (value == null) return "";
  return Number(value) >= 0 ? "pnl-positive" : "pnl-negative";
}

export function PortfolioDetailPage() {
  const { id } = useParams<{ id: string }>();
  const portfolio = usePortfolio(id);
  const positions = usePositions(id);

  if (portfolio.isLoading) {
    return (
      <div className="center-screen">
        <Spinner />
      </div>
    );
  }
  if (!portfolio.data) return <ErrorText error={portfolio.error} />;

  const p = portfolio.data;

  return (
    <Panel
      title={p.name}
      actions={
        <Link to={`/portfolios/${id}/transactions`}>
          <Button variant="ghost">Transactions</Button>
        </Link>
      }
    >
      <p className="muted">{PORTFOLIO_KIND_LABEL[p.kind]}</p>
      <div className="performance-row">
        <div className="stat-tile">
          <span className="stat-label">Rendement cible</span>
          <span className="stat-value">{fmtRate(p.targetReturn)}</span>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Performance globale</span>
          <span className={`stat-value ${pnlClass(p.summary.total_return_pct)}`}>
            {fmtPct(p.summary.total_return_pct)}
          </span>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Rendement annualisé pondéré</span>
          <span className={`stat-value ${pnlClass(p.summary.weighted_annualized_return)}`}>
            {fmtPct(p.summary.weighted_annualized_return)}
          </span>
        </div>
      </div>

      <div className="portfolio-summary">
        <div className="portfolio-card-row">
          <span className="muted">Valeur totale</span>
          <strong>
            {fmtAmount(p.summary.total_value)} {p.baseCurrency}
          </strong>
        </div>
        <div className="portfolio-card-row">
          <span className="muted">PV latente</span>
          <span>{fmtAmount(p.summary.unrealized_pnl)}</span>
        </div>
        <div className="portfolio-card-row">
          <span className="muted">PV réalisée</span>
          <span>{fmtAmount(p.summary.realized_pnl)}</span>
        </div>
        <div className="portfolio-card-row">
          <span className="muted">Solde espèces</span>
          <span>{fmtAmount(p.summary.cash_balance)}</span>
        </div>
      </div>

      <h3 className="panel-subhead">Positions</h3>
      <ErrorText error={positions.error} />
      {positions.isLoading ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Titre</th>
                <th>Qté</th>
                <th>CUMP</th>
                <th>Cours</th>
                <th>Valeur</th>
                <th>PV latente %</th>
                <th>XIRR</th>
                <th>Au</th>
              </tr>
            </thead>
            <tbody>
              {positions.data?.map((pos) => (
                <tr key={pos.security_id}>
                  <td>
                    {pos.ticker} — {pos.name}
                  </td>
                  <td>{fmtAmount(pos.quantity)}</td>
                  <td>{fmtPrice(pos.cump)}</td>
                  <td>{pos.market_price ? fmtPrice(pos.market_price) : "—"}</td>
                  <td>{fmtAmount(pos.market_value)}</td>
                  <td>{fmtPct(pos.unrealized_pnl_pct)}</td>
                  <td>{fmtPct(pos.annualized_return)}</td>
                  <td>{fmtDate(pos.price_date)}</td>
                </tr>
              ))}
              {positions.data?.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted">
                    Aucune position.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
